import React, {
  useContext,
  useCallback,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import {
  Paper,
} from '@mui/material';
import {
  LocalizationProvider,
} from '@mui/x-date-pickers';
import {
  AdapterMoment,
} from '@mui/x-date-pickers/AdapterMoment';
import Draggable from 'react-draggable';

import ChatChannelContext from '../../context/ChatChannelContext';
import { CHAT_MESSAGE_REQUEST_STATUS } from '../../Model/ChatMessageRequest';

import Tabs from '../Tabs';
import FormScheduleMessage from './components/FormScheduleMessage';
import ScheduledMessagesList from './components/ViewScheduledMessages';

import texts from './texts.json';
import {
  StyledModal,
  Container,
  CloseButton,
  ModalHeader,
  ModalContent,
  StyledChatIcon,
  ModalContainer,
} from './styles';

const SCHEDULER_MODAL_TAB = {
  NEW: 'NEW',
  VIEW: 'VIEW',
  PENDING: 'PENDING',
};

const DRAG_HANDLE_ID = 'message-scheduler-modal-drag-handle';

const DraggablePaper = (props) => {
  const nodeRef = useRef(null);
  return (
    <Draggable
      nodeRef={nodeRef}
      handle={`#${DRAG_HANDLE_ID}`}
      cancel="button, input, textarea, [role='button'], [contenteditable='true']"
    >
      <Paper ref={nodeRef} {...props} />
    </Draggable>
  );
};

const MessageSchedulerModal = ({
  isOpen,
  currentTab,
  setCurrentTab,
  onDidDismiss,
  showMessageScheduled,
  deleteOnly,
}) => {
  const {
    scheduleMessage,
    setScheduledCount,
    activeChannel,
  } = useContext(ChatChannelContext);

  const onTabChange = useCallback((value) => {
    setCurrentTab(value);
  }, [setCurrentTab]);

  const handleScheduleMessage = useCallback(async (sendAt, text, attachments, shouldForceSend) => {
    await scheduleMessage(sendAt, text, attachments, shouldForceSend);
    setScheduledCount((prevCount) => prevCount + 1);
    showMessageScheduled();
    setCurrentTab(SCHEDULER_MODAL_TAB.VIEW);
  }, [scheduleMessage, setCurrentTab, setScheduledCount, showMessageScheduled]);

  return (
    <StyledModal
      open={isOpen}
      onClose={onDidDismiss}
      disableEnforceFocus
      disablePortal
      maxWidth="md"
      PaperComponent={DraggablePaper}
    >
      <ModalContainer>
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <Container id={DRAG_HANDLE_ID}>
            <CloseButton onClick={onDidDismiss} />
            <ModalHeader>
              <Tabs
                tabsConfig={Object.fromEntries([
                  [SCHEDULER_MODAL_TAB.NEW, {
                    label: texts.tab.scheduleNewMessage + activeChannel.name,
                    disabled: deleteOnly,
                    tooltip: deleteOnly ? texts.tab.disabledTabTooltip : '',
                  }],
                  [SCHEDULER_MODAL_TAB.VIEW, {
                    label: texts.tab.viewScheduledMessages + activeChannel.name,
                    disabled: deleteOnly,
                    tooltip: deleteOnly ? texts.tab.disabledTabTooltip : '',
                  }],
                  [SCHEDULER_MODAL_TAB.PENDING, {
                    label: texts.tab.pendingScheduledMessages + activeChannel.name,
                  }],
                ])}
                selectedTab={currentTab}
                onSelectTab={onTabChange}
              />
            </ModalHeader>
            <ModalContent
              hidden={currentTab !== SCHEDULER_MODAL_TAB.NEW}
            >
              <FormScheduleMessage
                onSave={handleScheduleMessage}
                saveButtonLabel={(
                  <>
                    <StyledChatIcon />
                    {texts.button}
                  </>
                )}
              />
            </ModalContent>
            <ModalContent
              hidden={currentTab !== SCHEDULER_MODAL_TAB.VIEW}
            >
              <ScheduledMessagesList />
            </ModalContent>
            <ModalContent
              hidden={currentTab !== SCHEDULER_MODAL_TAB.PENDING}
            >
              <ScheduledMessagesList type={CHAT_MESSAGE_REQUEST_STATUS.PENDING} deleteOnly={deleteOnly} />
            </ModalContent>
          </Container>
        </LocalizationProvider>
      </ModalContainer>
    </StyledModal>
  );
};

MessageSchedulerModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  currentTab: PropTypes.string.isRequired,
  setCurrentTab: PropTypes.func.isRequired,
  onDidDismiss: PropTypes.func.isRequired,
  showMessageScheduled: PropTypes.func.isRequired,
  deleteOnly: PropTypes.bool,
};

MessageSchedulerModal.defaultProps = {
  deleteOnly: false,
};

export default MessageSchedulerModal;
export {
  SCHEDULER_MODAL_TAB,
};
